import React, { useEffect } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, Popover } from 'antd';
import FormattedNumber from 'src/components/common/FormattedNumber';
import ProductInfo from 'src/types/ProductInfo';
import { useOptionsFiltersFetching } from 'src/components/ProductFinder/OptionsFiltersSider/hooks';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import AddRequestMatch from './AddRequestMatch/AddRequestMatch';
import { getVarianceColor } from 'src/utils';
import { useAppStateContext, useAppDispatchContext } from 'src/stateProviders/useAppStateContext';
import { useRegionStateContext } from 'src/stateProviders/useRegionStateContext';
import { ACTION_TYPES, AppState } from 'src/stateProviders/appStateProvider';
import ProductDetails from './ProductDetails';
import styles from './ProductSummary.module.less';

type ProductSummaryProps = {
  sourceOwnerId: string;
  isReadOnlyMatch: boolean;
  data?: ProductInfo;
  className?: string;
  isScroll?: boolean;
};

const PRICE_TYPE = {
  source_regular_price: 'Regular Price',
  source_cart_price: 'Cart Price',
  source_coupon_price: 'Coupon Price',
  competitor_regular_price: 'Regular Price',
  competitor_cart_price: 'Cart Price',
  competitor_coupon_price: 'Coupon Price',
};
const REGULAR_PRICE_TYPE = '1';
const varianceRegularPriceTooltip =
  'This indicates the variance between the source product regular price and the median regular price of all competitor matches';
const varianceLowestPriceTooltip =
  'This indicates the variance between the source product lowest price and the median lowest price of all competitor matches';

const getPriceTypeTooltip = (indicator: string | undefined): string => {
  const labelTag = PRICE_TYPE[indicator as keyof typeof PRICE_TYPE];
  if (!indicator || !labelTag) {
    return 'Sale Price';
  }
  return labelTag;
};

const transformVariance = (value: string): number => {
  const variance = Number.parseFloat(value);
  if (Number.isFinite(variance)) {
    // TODO: remove multiplication with 100 when back-end is fixed
    return variance;
  }
  return NaN;
};

const getVarianceValue = (variance: number): string => {
  if (Number.isNaN(variance)) {
    return 'n/a';
  }
  const varianceString = `${variance.toFixed()} %`;
  return variance > 0 ? `+${varianceString}` : varianceString;
};

const ProductSummary: React.FC<ProductSummaryProps> = ({
  sourceOwnerId,
  isReadOnlyMatch,
  data,
  className,
  isScroll,
}) => {
  const { priceType, region } = useQueryUrlParams();
  const { user } = useAppStateContext();
  const { regionName } = useRegionStateContext();
  const { data: regionData } = useOptionsFiltersFetching(sourceOwnerId);
  const appDispatch = useAppDispatchContext();

  useEffect(() => {
    if (regionData?.Regions) {
      let label = '';
      if (region && region !== regionName) {
        regionData.Regions.some((regionGroup) => {
          label = regionGroup.region.find(({ value }) => value === region)?.label ?? '';
          return !!label;
        });
      }
      if (regionName !== label) {
        appDispatch({ type: ACTION_TYPES.setRegionName, payload: { regionName: label } as AppState });
      }
    }
  }, [region, regionData]);

  if (!data) {
    return null;
  }

  const { currency, imageURL, name, lowestVariance, lowestPrice, lowestPriceIndicator, ownerName, price, variance } =
    data;
    console.log("dataprojectUrl",data)
  const varianceOfPriceType = transformVariance(priceType !== REGULAR_PRICE_TYPE ? lowestVariance : variance);
  const varianceValue = getVarianceValue(varianceOfPriceType);
  const varianceColor = getVarianceColor(varianceOfPriceType);
  const isProductImageVisible = user?.appPermissions['display_product_image'];
  const varianceInfo = priceType === REGULAR_PRICE_TYPE ? varianceRegularPriceTooltip : varianceLowestPriceTooltip;
  const priceTypeInfo = getPriceTypeTooltip(lowestPriceIndicator);

  const handleScroll = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className={styles.actionsContainer}>
        <h5 className={styles.title}>
          Your product:
        </h5>
        {!isReadOnlyMatch && <AddRequestMatch />}
      </div>
      <div className={clsx(styles.productSummaryContainer, className)}>
        <div className={styles.productSummaryBlock}>
          {isProductImageVisible && !!imageURL && (
            <p className={styles.imageContainer}>
              <img src={imageURL} alt={name} />
            </p>
          )}
          <div className={styles.ownerNameContainer}>
            <span className={styles.label}>{ownerName}</span>
            <a href={data.productURL} rel="noopener noreferrer" target={'_blank'}>
              <span className={styles.name}>{name}</span>
            </a>
            <ProductDetails data={data} />
          </div>
        </div>
        <div className={styles.productSummaryBlock}>
          <p>
            <span className={styles.label}>price</span>
            {lowestPrice === price ? (
              <FormattedNumber type="currency" className={styles.price} currency={currency} value={price} />
            ) : (
              <>
                <span className={styles.priceWrapper}>
                  <Popover content={priceTypeInfo} trigger={'hover'} placement="left">
                    <Icon type="tag" theme="filled" />
                  </Popover>
                  <FormattedNumber type="currency" className={styles.price} currency={currency} value={lowestPrice} />
                </span>
                <FormattedNumber type="currency" className={styles.priceRegular} currency={currency} value={price} />
              </>
            )}
          </p>
          {regionName && (
            <p>
              <span className={styles.label}>Region</span>
              <span className={styles.region}>{regionName}</span>
            </p>
          )}
          <p>
            <span className={styles.label}>variance</span>
            <Popover content={<div className={styles.popover}>{varianceInfo}</div>} trigger={'hover'} placement="left">
              <span className={styles.variance} style={{ color: varianceColor }}>
                {varianceValue}
              </span>
            </Popover>
          </p>
        </div>
        {isScroll && (
          <span className={styles.toTop} onClick={handleScroll}>
            <FontAwesomeIcon icon={['far', 'arrow-up']} className={styles.arrowUp} />
            Top
          </span>
        )}
      </div>
    </>
  );
};

export default ProductSummary;
